import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { getOwners, getSubsidiaries } from "../../data/request";
import { OwnershipEntry } from "../../types/Ownership";
import { DELTA_ACCENT, DELTA_PRIMARY, TEXT_SECONDARY } from "../colors";
import { GroupStructureChart } from "./GroupStructureChart";
import { OwnershipTable } from "./OwnershipTable";

const SECTION_IDS = {
  chart: "group-structure-chart",
  owners: "group-structure-owners",
  subsidiaries: "group-structure-subsidiaries",
};

const scrollToSection = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const GroupStructurePanel = (props: { companyNo: string | undefined; companyName: string }) => {
  const { companyNo, companyName } = props;
  const [owners, setOwners] = useState<OwnershipEntry[]>([]);
  const [subsidiaries, setSubsidiaries] = useState<OwnershipEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (companyNo == undefined) {
        return;
      }
      setLoading(true);
      const [ownersDoc, subsidiariesDoc] = await Promise.all([getOwners(companyNo), getSubsidiaries(companyNo)]);
      setOwners(ownersDoc?.items ?? []);
      setSubsidiaries(subsidiariesDoc?.items ?? []);
      setLoading(false);
    };

    fetchData();
  }, [companyNo]);

  if (loading) {
    return (
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 200,
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (owners.length === 0 && subsidiaries.length === 0) {
    return (
      <Box style={{ padding: 20 }}>
        <Typography variant="body2" style={{ color: TEXT_SECONDARY }}>
          No corporate owners or subsidiaries recorded for this company.
        </Typography>
      </Box>
    );
  }

  const sectionLinks = [
    { id: SECTION_IDS.chart, label: "Structure", visible: true },
    { id: SECTION_IDS.owners, label: "Owners", visible: owners.length > 0 },
    { id: SECTION_IDS.subsidiaries, label: "Subsidiaries", visible: subsidiaries.length > 0 },
  ].filter((section) => section.visible);

  return (
    <Box>
      <Box style={{ display: "flex", gap: 8, flexWrap: "wrap", padding: "20px 20px 12px" }}>
        {sectionLinks.map((section) => (
          <Chip
            key={section.id}
            label={section.label}
            size="small"
            variant="outlined"
            onClick={() => scrollToSection(section.id)}
            style={{ borderColor: DELTA_ACCENT, color: DELTA_ACCENT }}
          />
        ))}
      </Box>

      <Box id={SECTION_IDS.chart} style={{ scrollMarginTop: 16 }}>
        <GroupStructureChart companyName={companyName} owners={owners} subsidiaries={subsidiaries} />
      </Box>

      {owners.length > 0 && (
        <Box id={SECTION_IDS.owners} style={{ scrollMarginTop: 16 }}>
          <Typography variant="subtitle1" style={{ color: DELTA_PRIMARY, fontWeight: 700, padding: "20px 20px 0" }}>
            Owners
          </Typography>
          <OwnershipTable entries={owners} />
        </Box>
      )}

      {subsidiaries.length > 0 && (
        <Box id={SECTION_IDS.subsidiaries} style={{ scrollMarginTop: 16 }}>
          <Typography variant="subtitle1" style={{ color: DELTA_PRIMARY, fontWeight: 700, padding: "20px 20px 0" }}>
            Subsidiaries
          </Typography>
          <OwnershipTable entries={subsidiaries} />
        </Box>
      )}
    </Box>
  );
};

export default GroupStructurePanel;
